/**
 * BEPRO Shop - Cart modul
 * localStorage-bazirani cart sa AJAX validacijom
 */

import { showToast } from './toast';

const STORAGE_KEY = 'bepro_cart';

/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(str) {
	if (typeof str !== 'string') return str;
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
}

const Cart = {
	items: [],

	/**
	 * Inicijalizacija - učitaj iz localStorage
	 */
	init() {
		this.load();
		this.updateUI();
		this.bindEvents();
	},

	/**
	 * Učitaj cart iz localStorage
	 */
	load() {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			this.items = stored ? JSON.parse(stored) : [];
		} catch (e) {
			this.items = [];
		}
	},

	/**
	 * Spremi cart u localStorage
	 */
	save() {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
		this.updateUI();
		document.dispatchEvent(new CustomEvent('bepro:cart:updated', { detail: { items: this.items } }));
	},

	/**
	 * Generiši unikatan key za stavku (id + veličina + boja)
	 */
	getItemKey(item) {
		return `${item.id}_${item.size || ''}_${item.color || ''}`;
	},

	/**
	 * Dodaj proizvod u korpu
	 */
	addItem(product) {
		const key = this.getItemKey(product);
		const existing = this.items.find(item => this.getItemKey(item) === key);

		if (existing) {
			existing.quantity += (product.quantity || 1);
		} else {
			this.items.push({
				id: product.id,
				title: product.title,
				image: product.image || '',
				size: product.size || '',
				color: product.color || '',
				price: parseFloat(product.price) || 0,
				quantity: product.quantity || 1,
				currency: product.currency || 'KM',
				url: product.url || '',
			});
		}

		this.save();
		document.dispatchEvent(new CustomEvent('bepro:cart:item-added'));
		return true;
	},

	/**
	 * Ukloni stavku iz korpe
	 */
	removeItem(key) {
		this.items = this.items.filter(item => this.getItemKey(item) !== key);
		this.save();
	},

	/**
	 * Ažuriraj količinu
	 */
	updateQuantity(key, quantity) {
		const item = this.items.find(item => this.getItemKey(item) === key);
		if (item) {
			item.quantity = Math.max(1, parseInt(quantity) || 1);
			this.save();
		}
	},

	/**
	 * Isprazni korpu
	 */
	clear() {
		this.items = [];
		this.save();
	},

	/**
	 * Ukupan broj stavki
	 */
	getCount() {
		return this.items.reduce((sum, item) => sum + item.quantity, 0);
	},

	/**
	 * Ukupna cijena (subtotal)
	 */
	getSubtotal() {
		return this.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
	},

	/**
	 * Formatiraj cijenu
	 */
	formatPrice(amount) {
		const currency = (this.items.length > 0 && this.items[0].currency)
			? this.items[0].currency
			: (window.beproShop ? window.beproShop.currency : 'KM');
		return amount.toFixed(2) + ' ' + currency;
	},

	/**
	 * Ažuriraj sve UI elemente
	 */
	updateUI() {
		// Mini-cart badge
		const badges = document.querySelectorAll('.js-cart-count');
		const count = this.getCount();
		badges.forEach(badge => {
			badge.textContent = count;
			badge.style.display = count > 0 ? 'flex' : 'none';
		});

		// Mini-cart dropdown
		this.renderMiniCart();

		// Cart stranica
		this.renderCartPage();
	},

	/**
	 * Renderuj mini-cart dropdown
	 */
	renderMiniCart() {
		const container = document.querySelector('.js-mini-cart-items');
		if (!container) return;

		const emptyMsg = document.querySelector('.js-mini-cart-empty');
		const footer = document.querySelector('.js-mini-cart-footer');
		const subtotalEl = document.querySelector('.js-mini-cart-subtotal');

		if (this.items.length === 0) {
			container.innerHTML = '';
			if (emptyMsg) emptyMsg.style.display = 'block';
			if (footer) footer.style.display = 'none';
			return;
		}

		if (emptyMsg) emptyMsg.style.display = 'none';
		if (footer) footer.style.display = 'block';

		container.innerHTML = this.items.map(item => {
			const key = escapeHtml(this.getItemKey(item));
			const details = [item.size, item.color].filter(Boolean).map(escapeHtml).join(' / ');
			return `
				<div class="mini-cart__item" data-key="${key}">
					<div class="mini-cart__item-image">
						${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}">` : ''}
					</div>
					<div class="mini-cart__item-info">
						<p class="mini-cart__item-title">${escapeHtml(item.title)}</p>
						${details ? `<p class="mini-cart__item-details">${details}</p>` : ''}
						<p class="mini-cart__item-price">${item.quantity} × ${this.formatPrice(item.price)}</p>
					</div>
					<button class="mini-cart__item-remove js-cart-remove" data-key="${key}" aria-label="Ukloni">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
						</svg>
					</button>
				</div>
			`;
		}).join('');

		if (subtotalEl) {
			subtotalEl.textContent = this.formatPrice(this.getSubtotal());
		}
	},

	/**
	 * Renderuj punu cart stranicu
	 */
	renderCartPage() {
		const container = document.querySelector('.js-cart-page-items');
		if (!container) return;

		const emptyMsg = document.querySelector('.js-cart-page-empty');
		const summary = document.querySelector('.js-cart-summary');
		const subtotalEl = document.querySelector('.js-cart-page-subtotal');
		const shippingEl = document.querySelector('.js-cart-page-shipping');
		const totalEl = document.querySelector('.js-cart-page-total');

		if (this.items.length === 0) {
			container.innerHTML = '';
			if (emptyMsg) emptyMsg.style.display = 'block';
			if (summary) summary.style.display = 'none';
			return;
		}

		if (emptyMsg) emptyMsg.style.display = 'none';
		if (summary) summary.style.display = 'block';

		container.innerHTML = this.items.map(item => {
			const key = escapeHtml(this.getItemKey(item));
			const details = [item.size, item.color].filter(Boolean).map(escapeHtml).join(' / ');
			const lineTotal = item.price * item.quantity;
			return `
				<div class="cart-item" data-key="${key}">
					<div class="cart-item__image">
						${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}">` : ''}
					</div>
					<div class="cart-item__info">
						<a href="${escapeHtml(item.url)}" class="cart-item__title font-semibold">${escapeHtml(item.title)}</a>
						${details ? `<p class="cart-item__details">${details}</p>` : ''}
						<p class="cart-item__unit-price">${this.formatPrice(item.price)}</p>
					</div>
					<div class="cart-item__quantity">
						<button class="cart-item__qty-btn js-cart-qty" data-key="${key}" data-action="minus">−</button>
						<input type="number" class="cart-item__qty-input js-cart-qty-input" data-key="${key}" value="${item.quantity}" min="1" max="99">
						<button class="cart-item__qty-btn js-cart-qty" data-key="${key}" data-action="plus">+</button>
					</div>
					<div class="cart-item__total">
						<p class="font-semibold">${this.formatPrice(lineTotal)}</p>
					</div>
					<button class="cart-item__remove js-cart-remove" data-key="${key}" aria-label="Ukloni">
						<svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
						</svg>
					</button>
				</div>
			`;
		}).join('');

		// Izračunaj ukupne iznose (chunk-based shipping)
		const subtotal = this.getSubtotal();
		const totalQty = this.items.reduce((sum, item) => sum + item.quantity, 0);
		const shipInfo = this.computeShipping(subtotal, totalQty);
		const shippingCost = shipInfo.cost;
		const total = subtotal + shippingCost;

		if (subtotalEl) subtotalEl.textContent = this.formatPrice(subtotal);
		if (shippingEl) {
			shippingEl.textContent = shippingCost > 0
				? this.formatPrice(shippingCost)
				: 'Besplatna';
		}
		if (totalEl) totalEl.textContent = this.formatPrice(total);

		// Multi-delivery notice
		const noticeEl = document.querySelector('.js-cart-page-multidelivery');
		if (noticeEl) {
			if (shipInfo.shipments > 1) {
				noticeEl.innerHTML = `
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
						<path d="M3 7h13v10H3zM16 10h4l1 3v4h-5"/>
						<circle cx="7" cy="19" r="2"/>
						<circle cx="18" cy="19" r="2"/>
					</svg>
					<span>Vaša narudžba će biti poslana u <strong>${shipInfo.shipments} odvojene pošiljke</strong> (maksimalno ${shipInfo.chunkSize} artikla po pošiljci). O svakoj pošiljci ćete biti obaviješteni.</span>
				`;
				noticeEl.style.display = 'flex';
			} else {
				noticeEl.style.display = 'none';
			}
		}
	},

	/**
	 * Per-item shipping: cost = totalQty * chunkCost (20 KM per item).
	 * shipments = ceil(totalQty / chunkSize) — informational only (max chunkSize per pošiljka).
	 */
	computeShipping(subtotal, totalQty) {
		const ship = (window.beproShop && window.beproShop.shipping) || {};
		const out = { cost: 0, shipments: 0, chunkSize: ship.chunk_size || 3, chunkCost: ship.chunk_cost || 20 };

		if (!ship.enabled || totalQty <= 0) return out;
		if (ship.free_above > 0 && subtotal >= ship.free_above) return out;

		out.shipments = Math.ceil(totalQty / out.chunkSize);
		out.cost = totalQty * out.chunkCost;
		return out;
	},

	/**
	 * Binduj globalne event listenere
	 */
	bindEvents() {
		// Delegated events za remove dugme
		document.addEventListener('click', (e) => {
			// Remove
			const removeBtn = e.target.closest('.js-cart-remove');
			if (removeBtn) {
				e.preventDefault();
				const key = removeBtn.dataset.key;
				this.removeItem(key);
				showToast("Proizvod uklonjen iz korpe", "info", 2500);
			}

			// Quantity +/-
			const qtyBtn = e.target.closest('.js-cart-qty');
			if (qtyBtn) {
				e.preventDefault();
				const key = qtyBtn.dataset.key;
				const action = qtyBtn.dataset.action;
				const item = this.items.find(i => this.getItemKey(i) === key);
				if (item) {
					const newQty = action === 'plus' ? item.quantity + 1 : item.quantity - 1;
					if (newQty < 1) {
						this.removeItem(key);
					} else {
						this.updateQuantity(key, newQty);
					}
				}
			}

			// Toggle mini-cart
			const cartToggle = e.target.closest('.js-cart-toggle');
			if (cartToggle) {
				e.preventDefault();
				const miniCart = document.querySelector('.js-mini-cart');
				if (miniCart) {
					miniCart.classList.toggle('is-open');
				}
			}
		});

		// Quantity input change
		document.addEventListener('change', (e) => {
			if (e.target.classList.contains('js-cart-qty-input')) {
				const key = e.target.dataset.key;
				const val = parseInt(e.target.value);
				if (val > 0) {
					this.updateQuantity(key, val);
				}
			}
		});

		// Otvori mini-cart kada se doda stavka
		document.addEventListener('bepro:cart:item-added', () => {
			const miniCart = document.querySelector('.js-mini-cart');
			if (miniCart) miniCart.classList.add('is-open');
		});

		// Zatvori mini-cart klikom van
		document.addEventListener('click', (e) => {
			const miniCart = document.querySelector('.js-mini-cart');
			if (miniCart && miniCart.classList.contains('is-open')) {
				if (!e.target.closest('.js-mini-cart') && !e.target.closest('.js-cart-toggle')) {
					miniCart.classList.remove('is-open');
				}
			}
		});
	},
};

export { escapeHtml };
export default Cart;
